import { LoadLiveChannels } from "shared/components/stored_fields/service_account_select_field/api"
import { windowFeatures } from "shared/reconnect_window"

import type { ServiceConnectionService } from "../types"

type LiveChannel = { id: string; offline: boolean }

function isConnected(channels: LiveChannel[], service: ServiceConnectionService): boolean {
  if (service.mode === "reconnect" && service.account) {
    const target = channels.find(lc => String(lc.id) === String(service.account?.account_id))
    if (target) return target.offline === false
  }
  return channels.some(lc => lc.offline === false)
}

async function detectConnected(service: ServiceConnectionService): Promise<boolean> {
  try {
    const response = await LoadLiveChannels(service.value)
    const channels = response?.data?.channel?.live_channels
    if (!Array.isArray(channels)) return false
    return isConnected(channels as LiveChannel[], service)
  } catch {
    return false
  }
}

function withCompletionReturn(rawUrl: string, service: ServiceConnectionService): string {
  const url = new URL(rawUrl, window.location.origin)
  if (!url.searchParams.has("return_to")) {
    // The completion page must live on the same origin the activation flow runs on: the
    // popup redirects to `return_to` at the end, and the flow rejects a return_to pointing
    // at a different host than where the connection happened. That host is whatever the
    // backend put in connect_url/reconnect_url (always production ifttt.com — local points
    // at prod too), so derive it from the URL itself rather than hardcoding a domain.
    url.searchParams.set("return_to", `${url.origin}/mcp/connection/complete?service=${service.value}`)
  }
  return url.toString()
}

// COOP headers can block the popup from messaging back, so detect completion on
// focus return and re-check live_channels — same approach as diy/service_connection.
export function connectService(service: ServiceConnectionService): Promise<boolean> {
  const rawUrl = service.mode === "reconnect" ? service.reconnect_url : service.connect_url
  if (!rawUrl) return Promise.resolve(false)
  const url = withCompletionReturn(rawUrl, service)

  return new Promise<boolean>(resolve => {
    const popup = window.open(url, `${service.value}_connection`, windowFeatures)
    if (!popup) {
      resolve(false)
      return
    }
    window.addEventListener("focus", () => void detectConnected(service).then(resolve), { once: true })
  })
}
